import axios from "axios";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import env from "../../config/environment.js";
import { useErrorHandler } from "../../hooks/useErrorHandler";
import { useAudioStore } from "../../stores/audioStore";
import { useAuthStore } from "../../stores/authStore";
import { useBookmarkStore } from "../../stores/bookmarkStore";
import { useHighlightStore } from "../../stores/highlightStore";
import "../../styles/App.css";
import "../../styles/ToolsPanel.css";
import { AUDIO_STATES } from "../../utils/constants";
import ColorPicker from "../ui/ColorPicker";
import { InlineLoader } from "../ui/LoadingSpinner";
import RichTextEditor from "../ui/RichTextEditor";

const ToolsPanel = memo(
  ({ activeTextSize, onSetTextSize, currentTopic, onPageChange, courseData }) => {
    // State and store hooks
    const [noteText, setNoteText] = useState("");
    const [noteKey, setNoteKey] = useState(""); // Add key to force re-render
    const [isSavingNote, setIsSavingNote] = useState(false);
    const [noteId, setNoteId] = useState(null); // Track note ID for updates
    const { addError } = useErrorHandler();
    const { currentUser } = useAuthStore();
    const {
      selectedColor,
      setSelectedColor,
      isLoading: isHighlightLoading,
    } = useHighlightStore();
    const { addBookmark, isPageBookmarked, isTopicBookmarked, isResourceBookmarked, getBookmarkByResourceId, removeBookmark, bookmarks, fetchBookmarks } =
      useBookmarkStore();
    const {
      isPlaying,
      playlist,
      trackIndex,
      audioState,
      error,
      play,
      pause,
      nextTrack,
      prevTrack,
      hasNextTrack,
      hasPrevTrack,
      setTrackIndex,
    } = useAudioStore();

    // Enhanced helper function to generate unique storage key for each page
    const generateStorageKey = useCallback((topic) => {
      if (!topic) return null;
      // Get the base topic ID
      const topicId = topic.realTopicId || topic.id;

      // Get current page information
      let pageIdentifier;

      if (topic.originalStructure && topic.originalStructure.pageId) {
        // If we have original structure with pageId, use it
        pageIdentifier = topic.originalStructure.pageId;
      } else if (topic.currentPageIndex !== undefined) {
        // Use currentPageIndex if available
        pageIdentifier = `page-${topic.currentPageIndex}`;
      } else if (topic.currentPage) {
        // Use currentPage if available
        pageIdentifier = `page-${topic.currentPage}`;
      } else {
        // Fallback to page 1
        pageIdentifier = "page-0";
      }

      // Create a unique key that includes subtopic info if available
      const subTopicId =
        topic.originalStructure?.subTopicId || topic.subTopicId || "";

      const storageKey = subTopicId
        ? `notes_${topicId}_${subTopicId}_${pageIdentifier}`
        : `notes_${topicId}_${pageIdentifier}`;

      return storageKey;
    }, []);

    // Load notes from API when page changes
    useEffect(() => {
      const fetchNotes = async () => {
        if (!currentTopic || !currentUser?.id) return;

        try {
          const config = await env;
          const topicId = currentTopic.realTopicId || currentTopic.id;
          const currentPageIndex = currentTopic.currentPageIndex || 0;
          const currentResource = currentTopic.resources?.[currentPageIndex];
          const resourceId = currentResource?.id || currentTopic.resourceId || currentTopic.id;

          const response = await axios.get(`${config.api.baseUrl}/notes`, {
            params: {
              userId: currentUser.id,
              resourceId: resourceId
            },
            headers: {
              'Authorization': `Bearer ${currentUser.token}`
            }
          });

          if (response.data && response.data.data) {
            const noteData = response.data.data;
            if (noteData.length > 0) {
              // Use the first note found for this topic
              setNoteText(noteData[0].content);
              setNoteId(noteData[0].id);
            } else {
              // No notes found, use empty string
              setNoteText("");
              setNoteId(null);
            }

            // Update key to force RichTextEditor re-render
            const storageKey = generateStorageKey(currentTopic);
            setNoteKey(storageKey);
          }
        } catch (error) {
          console.error("Error fetching notes:", error);

          // Fallback to localStorage if API fails
          const storageKey = generateStorageKey(currentTopic);
          if (storageKey) {
            const savedNotes = localStorage.getItem(storageKey) || "";
            setNoteText(savedNotes);
            setNoteKey(storageKey);
          }

          addError("Failed to load notes. Using local storage instead.");
        }
      };

      fetchNotes();
    }, [currentTopic, currentUser, generateStorageKey, addError]);

    // Save notes to localStorage as backup when noteText changes
    useEffect(() => {
      if (currentTopic && noteText !== undefined) {
        const storageKey = generateStorageKey(currentTopic);
        if (storageKey) {
          // Convert noteText to string if it's an object
          const noteContent = typeof noteText === 'object' ? JSON.stringify(noteText) : String(noteText || '');
          
          if (noteContent.trim()) {
            localStorage.setItem(storageKey, noteContent);
          } else {
            localStorage.removeItem(storageKey);
          }
        }
      }
    }, [noteText, currentTopic, generateStorageKey]);

    // Fetch bookmarks from API when currentTopic changes
    useEffect(() => {
      const loadBookmarks = async () => {
        if (currentTopic) {
          const currentPageIndex = currentTopic.currentPageIndex || 0;
          const currentResource = currentTopic.resources?.[currentPageIndex];
          const resourceId = currentResource?.id;

          if (resourceId) {
            try {
              await fetchBookmarks(resourceId);
            } catch (error) {
              console.error('Failed to load bookmarks:', error);
            }
          }
        }
      };

      loadBookmarks();
    }, [currentTopic, fetchBookmarks]);

    // Handle note text changes from RichTextEditor
    const handleNoteChange = useCallback((value) => {
      // If the value is a string (JSON string from RichTextEditor), parse it
      // If it's already an object, use it directly
      const content = typeof value === 'string' ? JSON.parse(value) : value;
      
      // Update the note text state
      setNoteText(content);
      
      // Also save to localStorage as backup
      if (currentTopic) {
        const storageKey = generateStorageKey(currentTopic);
        if (storageKey) {
          localStorage.setItem(storageKey, JSON.stringify(content));
        }
      }
    }, [currentTopic, generateStorageKey]);

    // Handle note saving to API
    const handleSaveNote = useCallback(async () => {
      // Convert noteText to string if it's an object
      const noteContent = typeof noteText === 'object' ? JSON.stringify(noteText) : String(noteText || '');
      
      if (!currentTopic || !currentUser?.id || !noteContent.trim()) {
        console.warn('Cannot save note: missing required data');
        return;
      }

      setIsSavingNote(true);
      try {
        const config = await env;
        const topicId = currentTopic.realTopicId || currentTopic.id;

        const currentPageIndex = currentTopic.currentPageIndex || 0;
        const currentResource = currentTopic.resources?.[currentPageIndex];
        const resourceId = currentResource?.id || currentTopic.resourceId || currentTopic.id;

        const payload = {
          userId: currentUser.id,
          topicId: topicId,
          resourceId: resourceId,
          courseId: currentTopic.courseId || (courseData?.id || ''),
          title: currentTopic.title || "Note",
          content: noteContent,
          tags: ""
        };
        
        console.log('Saving note with payload:', payload);

        let response;
        if (noteId) {
          // Update existing note
          response = await axios.patch(`${config.api.baseUrl}/notes/${noteId}`, payload, {
            headers: {
              'Authorization': `Bearer ${currentUser.token}`,
              'Content-Type': 'application/json'
            }
          });
        } else {
          // Create new note
          response = await axios.post(`${config.api.baseUrl}/notes`, payload, {
            headers: {
              'Authorization': `Bearer ${currentUser.token}`,
              'Content-Type': 'application/json'
            }
          });

          // Save the note ID for future updates
          if (response.data && response.data.data && response.data.data.id) {
            setNoteId(response.data.data.id);
          }
        }

        console.log("Note saved successfully:", response.data);
      } catch (error) {
        console.error("Error saving note:", error);
        addError("Failed to save note. Changes are still saved locally.");
      } finally {
        setIsSavingNote(false);
      }
    }, [currentTopic, currentUser, noteText, noteId, addError]);

    // Memoized calculations and state derivations
    const currentTrack = playlist[trackIndex] || {};
    const hasAudio = !!currentTrack?.audioSrc;

    // Updated pagination calculation in ToolsPanel.js
    const { currentPage, totalPages } = useMemo(() => {
      if (!currentTopic) {
        return { currentPage: 1, totalPages: 1 };
      }

      // Priority 1: Check if we have resources from the API response (new format)
      if (currentTopic.resources && currentTopic.resources.length > 0) {
        const currentPageIndex = currentTopic.currentPageIndex || 0;
        const currentResource = currentTopic.resources[currentPageIndex];

        // Use orderIndex from the current resource if available, otherwise fall back to array index + 1
        const displayPageNumber = currentResource?.orderIndex || (currentPageIndex + 1);

        return {
          currentPage: displayPageNumber,
          totalPages: currentTopic.resources.length,
        };
      }

      // Priority 2: Fallback to original structure if resources are not available (legacy format)
      if (currentTopic.originalStructure && currentTopic.originalStructure.totalPages) {
        const currentPageIndex = currentTopic.currentPageIndex || 0;
        return {
          currentPage: currentPageIndex + 1,
          totalPages: currentTopic.originalStructure.totalPages,
        };
      }

      // Priority 3: Single page fallback
      return { currentPage: 1, totalPages: 1 };
    }, [currentTopic]);

    // --- FULL HANDLER FUNCTIONS ---

    const handlePageClick = useCallback(
      (pageNumber) => {
        if (currentTopic && onPageChange) {
          onPageChange(currentTopic.id, pageNumber);
        }
      },
      [currentTopic, onPageChange]
    );

    const handleNextPage = useCallback(() => {
      if (currentPage < totalPages) {
        handlePageClick(currentPage + 1);
      }
    }, [currentPage, totalPages, handlePageClick]);

    const handleTextSizeChange = useCallback(
      (size) => {
        onSetTextSize(size);
      },
      [onSetTextSize]
    );

    const handleColorSelect = useCallback(
      (color) => {
        setSelectedColor(color);
      },
      [setSelectedColor]
    );

    const handleAudioControl = useCallback(
      async (action) => {
        try {
          if (action === "play") await play();
          else if (action === "pause") pause();
          else if (action === "next") nextTrack();
          else if (action === "prev") prevTrack();
        } catch (err) {
          addError("Audio control failed");
        }
      },
      [play, pause, nextTrack, prevTrack, addError]
    );

    const handleTrackSelect = useCallback(
      (index) => {
        setTrackIndex(index);
      },
      [setTrackIndex]
    );

    const createRobustPageId = useCallback((topic) => {
      if (!topic) return null;

      const topicId = topic.realTopicId || topic.id;
      const pageIndex = topic.currentPageIndex || 0;
      const subTopicId =
        topic.originalStructure?.subTopicId || topic.subTopicId || "";

      return topic.originalStructure
        ? `${topicId}-${subTopicId}-page-${pageIndex}`
        : `${topicId}-page-${pageIndex}`;
    }, []);

    const currentPageBookmarked = useMemo(() => {
      if (!currentTopic) return false;
      const currentPageIndex = currentTopic.currentPageIndex || 0;
      const currentResource = currentTopic.resources?.[currentPageIndex];
      const resourceId = currentResource?.id;

      if (resourceId) {
        return isResourceBookmarked(resourceId);
      }

      // Fallback to topic-based bookmarking
      const topicId = currentTopic.realTopicId || currentTopic.id;
      return isTopicBookmarked(topicId);
    }, [currentTopic, isResourceBookmarked, isTopicBookmarked, bookmarks]);

    const handleBookmarkToggle = useCallback(async () => {
      if (!currentTopic) return;
      const topicId = currentTopic.realTopicId || currentTopic.id;
      const pageId = createRobustPageId(currentTopic);
      const currentPageIndex = currentTopic.currentPageIndex || 0;
      const currentResource = currentTopic.resources?.[currentPageIndex];
      const resourceId = currentResource?.id;

      if (currentPageBookmarked) {
        // Use getBookmarkByResourceId to find the specific bookmark
        const bookmark = resourceId ? getBookmarkByResourceId(resourceId) : bookmarks.find((b) => b.topicId === topicId);
        if (bookmark) {
          try {
            await removeBookmark(bookmark.id);
          } catch (error) {
            console.error('Failed to remove bookmark:', error);
          }
        }
      } else {
        try {
          await addBookmark({
            topicId,
            pageId,
            title: currentTopic.title,
            pageNumber: currentPage,
            courseId: courseData?.id,
            resourceId: resourceId, // Use actual resource ID from /topics/{topicId} API response
          });
        } catch (error) {
          console.error('Failed to add bookmark:', error);
        }
      }
    }, [
      currentTopic,
      currentPageBookmarked,
      bookmarks,
      addBookmark,
      removeBookmark,
      getBookmarkByResourceId,
      createRobustPageId,
      currentPage,
      courseData?.id,
    ]);

    return (
      <aside className="tools-panel-sidebar">
        {/* 1. Page Section */}
        <div className="tool-section">
          <div className="tool-section-header">
            <span className="tool-section-title ">Page:</span>
          </div>
          <div className="page-controls-wrapper">
            <div className="pagination-controls">
              <button
                className="pagination-nav-btn"
                onClick={() => handlePageClick(currentPage - 1)}
                disabled={currentPage <= 1}
              >
                {"<"}
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                (pageNum) => (
                  <button
                    key={pageNum}
                    className={`pagination-btn ${pageNum === currentPage ? "active" : ""
                      }`}
                    onClick={() => handlePageClick(pageNum)}
                  >
                    {pageNum}
                  </button>
                )
              )}
              <button
                className="pagination-nav-btn"
                onClick={handleNextPage}
                disabled={currentPage >= totalPages}
              >
                {">"}
              </button>
            </div>
            <button
              className="add-bookmark-btn"
              onClick={handleBookmarkToggle}
              title={currentPageBookmarked ? "Remove bookmark" : "Add bookmark"}
              aria-pressed={currentPageBookmarked}
            >
              {currentPageBookmarked ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  width="24"
                  height="24"
                  aria-hidden="true"
                >
                  <path d="M6 4v16l6-6 6 6V4z" />
                </svg>
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  width="24"
                  height="24"
                  aria-hidden="true"
                >
                  <path d="M6 4v16l6-6 6 6V4z" />
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* 2. Text Size Section */}
        <div className="tool-section">
          <div className="tool-section-header">
            <span className="tool-section-title">Text size:</span>
          </div>
          <div className="text-size-controls">
            <button
              className={`text-size-btn ${activeTextSize === "small" ? "active" : ""
                }`}
              onClick={() => handleTextSizeChange("small")}
            >
              Small
            </button>
            <button
              className={`text-size-btn ${activeTextSize === "normal" ? "active" : ""
                }`}
              onClick={() => handleTextSizeChange("normal")}
            >
              Normal
            </button>
            <button
              className={`text-size-btn ${activeTextSize === "large" ? "active" : ""
                }`}
              onClick={() => handleTextSizeChange("large")}
            >
              Large
            </button>
          </div>
        </div>

        {/* 3. Now Playing Section */}
        {hasAudio && (
          <div className="tool-section">
            <div className="tool-section-header">
              <span className="tool-section-title">Now playing:</span>
            </div>

            <div className="media-player">
              <div className="media-info">
                <span className="media-title">
                  {currentTrack.title || "Audio Track"}
                </span>
              </div>

              <div className="media-controls">
                {/* Previous Button */}
                <button
                  onClick={() => handleAudioControl("prev")}
                  className="media-control-btn"
                  disabled={
                    !hasPrevTrack() || audioState === AUDIO_STATES.LOADING
                  }
                  title="Previous"
                >
                  <span className="control-icon" role="img" aria-label="Previous">
                    ⏮
                  </span>
                </button>

                {/* Play / Pause Button */}
                <button
                  onClick={() => handleAudioControl(isPlaying ? "pause" : "play")}
                  className="media-control-btn play-pause"
                  disabled={!hasAudio}
                  title={isPlaying ? "Pause" : "Play"}
                >
                  {audioState === AUDIO_STATES.LOADING ? (
                    <InlineLoader size="small" />
                  ) : (
                    <span
                      className="control-icon"
                      role="img"
                      aria-label={isPlaying ? "Pause" : "Play"}
                    >
                      {isPlaying ? "⏸" : "▶"}
                    </span>
                  )}
                </button>

                {/* Next Button */}
                <button
                  onClick={() => handleAudioControl("next")}
                  className="media-control-btn"
                  disabled={
                    !hasNextTrack() || audioState === AUDIO_STATES.LOADING
                  }
                  title="Next"
                >
                  <span className="control-icon" role="img" aria-label="Next">
                    ⏭
                  </span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 4. Audio List Section */}

        {/* <div className="section-header">
          <span className="section-title">Audio List:</span>
        </div>
        {playlist.length > 0 && (
          <div className="tool-section">
            <div className="audio-list">
              {playlist.map((track, index) => (
                <div
                  key={index}
                  className={`audio-item ${
                    index === trackIndex ? "active" : ""
                  }`}
                  onClick={() => handleTrackSelect(index)}
                >
                  <span className="audio-item-title">
                    {track.title || `Audio ${index + 1}`}
                  </span>
                  {index === trackIndex && isPlaying && (
                    <div className="audio-playing-indicator">
                      <div className="audio-wave">
                        <div className="wave-bar"></div>
                        <div className="wave-bar"></div>
                        <div className="wave-bar"></div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )} */}

        {/* 5. Annotate Section with Separator */}
        <div className="tool-section annotate-section has-separator">
          <div className="tool-section-header">
            <span className="tool-section-title ">Annotate:</span>
          </div>
          <div className="annotate-controls">
            <div className="highlighter-container">
              <img
                src="/highlighter.png"
                alt="Highlighter"
                className="highlighter-icon"

              />
            </div>
            <ColorPicker
              selectedColor={selectedColor}
              onColorSelect={handleColorSelect}
              disabled={isHighlightLoading}
            />
          </div>
        </div>

        <hr className="tools-separator" />

        {/* 6. Notes Section */}
        <div className="tool-section notes-section">
          <RichTextEditor
            key={noteKey} // Force re-render when page changes
            value={noteText}
            onChange={handleNoteChange}
            placeholder="Write your notes here...."
            onSave={handleSaveNote}
            isSaving={isSavingNote}
          />
        </div>
      </aside>
    );
  }
);

ToolsPanel.displayName = "ToolsPanel";
export default ToolsPanel;
